import { AlertCircle, Clock, RotateCw } from 'lucide-react';
import { ScreenerApiError } from '@/lib/api/screenerApi';

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
}

export function errorStatus(error: unknown): number | null {
  return error instanceof ScreenerApiError ? error.status : null;
}

export default function ErrorState({ error, onRetry, compact = false }: ErrorStateProps) {
  const status = errorStatus(error);
  const rateLimited = status === 429;

  const title = rateLimited
    ? 'Too many requests'
    : status === 503
      ? 'Data is taking a short break'
      : 'Something went wrong';
  const message = rateLimited
    ? 'You have made more than 60 requests in a minute. Wait a minute, then try again.'
    : status === 503
      ? 'Our data store is temporarily unavailable. Please try again in a little while.'
      : error instanceof Error
        ? error.message
        : 'Could not load the data.';

  const Icon = rateLimited ? Clock : AlertCircle;

  return (
    <div
      role="alert"
      className={`flex flex-col items-center justify-center rounded-2xl border text-center ${
        rateLimited
          ? 'border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/[0.06]'
          : 'border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/[0.06]'
      } ${compact ? 'px-4 py-6' : 'px-6 py-14'}`}
    >
      <Icon className={`h-7 w-7 ${rateLimited ? 'text-amber-500' : 'text-red-500'}`} />
      <p className="mt-3 text-base font-semibold text-zinc-900 dark:text-white">{title}</p>
      <p className="mt-1 max-w-md text-sm text-zinc-600 dark:text-zinc-400">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-zinc-900 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-85 dark:bg-white dark:text-zinc-900"
        >
          <RotateCw className="h-4 w-4" />
          Try again
        </button>
      )}
    </div>
  );
}
